import React, { useState, useEffect, useCallback } from 'react';
import { tokens as tk } from './theme';
import SignaturePad from './components/SignaturePad';
import AnswerSheet from './components/AnswerSheet';

const API = 'https://tcio-api.onrender.com';
const DOT = '\u00B7', DASH = '\u2014', CHECK = '\u2713';
const card = { background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, boxShadow: '0 2px 8px rgba(34,48,62,0.06)' };
const lab = { fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: tk.textSecondary, fontWeight: 600 };

function useIsWide() {
  const [w, setW] = useState(typeof window !== 'undefined' && window.innerWidth >= 900);
  useEffect(() => { const h = () => setW(window.innerWidth >= 900);
    window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return w;
}

export default function StaffOnboardPage({ token }) {
  const isWide = useIsWide();
  const [state, setState] = useState(null);
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(true);
  const [idx, setIdx] = useState(0);
  const [sigName, setSigName] = useState('');
  const [formVals, setFormVals] = useState({});
  const [sigImg, setSigImg] = useState(null);
  const [padKey, setPadKey] = useState(0);
  const [docUrl, setDocUrl] = useState(null);
  const [docErr, setDocErr] = useState('');
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [stepErr, setStepErr] = useState('');
  const [justFinished, setJustFinished] = useState(false);

  const load = useCallback(async (goFirstOpen) => {
    try {
      const j = await fetch(API + '/api/staff-onboard/' + token).then(r => r.json());
      if (!j.ok) { setErr(j.error || 'This link is invalid.'); setLoading(false); return; }
      setState(j);
      if (goFirstOpen) {
        const first = j.steps.findIndex(s => !s.done);
        setIdx(first === -1 ? j.steps.length : first);
      }
    } catch (e) { setErr('Could not reach the server. Check your connection and reload.'); }
    setLoading(false);
  }, [token]);

  // W4-G: load the step's template document (Kelly's actual form) so it renders inline above the signature
  useEffect(() => {
    setDocUrl(null); setDocErr('');
    const list = (state && state.steps) || [];
    const s = idx < list.length ? list[idx] : null;
    if (!s || !s.has_document) return;
    let alive = true;
    fetch(API + '/api/staff-onboard/' + token + '/step/' + s.requirement_id + '/document')
      .then(r => r.json())
      .then(j => { if (!alive) return; if (j.ok && j.url) setDocUrl(j.url); else setDocErr(j.error || 'unavailable'); })
      .catch(() => { if (alive) setDocErr('network'); });
    return () => { alive = false; };
  }, [idx, state, token]);
  useEffect(() => { load(true); }, [load]);
  useEffect(() => {
    const s = state && idx < state.steps.length ? state.steps[idx] : null;
    setFormVals(s && s.answers ? s.answers : {});
  }, [idx, state]);

  const btn = (primary, disabled) => ({ font: 'inherit', fontSize: 15, fontWeight: 600, padding: '13px 18px', borderRadius: tk.radiusControl,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1, width: '100%', minHeight: 48,
    border: '1px solid ' + (primary ? tk.ai : tk.border), background: primary ? tk.ai : tk.surface, color: primary ? tk.surface : tk.ink });
  const inp = { font: 'inherit', fontSize: 16, padding: '12px 14px', borderRadius: tk.radiusControl, border: '1px solid ' + tk.border,
    color: tk.ink, background: tk.surface, width: '100%', boxSizing: 'border-box' };

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', background: tk.canvas, fontFamily: tk.font, padding: '18px 14px 60px' }}>
      <div style={{ maxWidth: isWide ? 1040 : 560, margin: '0 auto' }}>
        <div style={{ fontSize: 17, fontWeight: 800, color: tk.ink, letterSpacing: '-0.3px', marginBottom: 2 }}>TCIO</div>
        {children}
      </div>
    </div>);

  if (loading) return wrap(<div style={{ ...card, height: 160, background: tk.surfaceSoft, marginTop: 14 }} />);
  if (err) return wrap(
    <div style={{ ...card, padding: 20, marginTop: 14 }}>
      <div style={{ fontSize: 16, fontWeight: 700, color: tk.ink, marginBottom: 6 }}>Link problem</div>
      <div style={{ fontSize: 14, color: tk.textSecondary }}>{err}</div>
    </div>);

  const steps = state.steps;
  const done = steps.filter(s => s.done).length;
  const pct = steps.length ? Math.round(done / steps.length * 100) : 0;
  const allDone = done === steps.length;
  const submitted = state.status === 'submitted' || justFinished;

  const setFV = (k) => (e) => setFormVals({ ...formVals, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });
  const renderField = (f) => {
    const v = formVals[f.key];
    const base = { ...inp, fontSize: 15 };
    return (
      <div key={f.key} style={{ marginBottom: 12 }}>
        <div style={{ ...lab, marginBottom: 4 }}>{f.label}{f.required ? ' *' : ''}</div>
        {f.type === 'radio' ? (f.options || []).map(o => (
          <label key={o} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, color: tk.ink, padding: '5px 0', cursor: 'pointer' }}>
            <input type="radio" name={f.key} checked={v === o} onChange={() => setFormVals({ ...formVals, [f.key]: o })} style={{ marginTop: 3 }} />
            <span>{o}</span>
          </label>))
        : f.type === 'checkbox' ? (
          <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, color: tk.ink, cursor: 'pointer' }}>
            <input type="checkbox" checked={!!v} onChange={setFV(f.key)} style={{ marginTop: 3 }} />
            <span>Yes</span>
          </label>)
        : f.type === 'date' ? <input type="date" style={base} value={v || ''} onChange={setFV(f.key)} />
        : f.type === 'money' ? <input type="number" inputMode="decimal" step="0.01" min="0" placeholder="0.00" style={base} value={v === undefined ? '' : v} onChange={setFV(f.key)} />
        : f.type === 'number' ? <input type="number" inputMode="numeric" step="1" min="0" style={base} value={v === undefined ? '' : v} onChange={setFV(f.key)} />
        : f.type === 'ssn' ? <input inputMode="numeric" autoComplete="off" placeholder="___-__-____" style={base} value={v || ''} onChange={setFV(f.key)} />
        : <input style={base} value={v || ''} onChange={setFV(f.key)} autoComplete={f.key.includes('address') ? 'street-address' : f.key === 'phone' ? 'tel' : 'off'} />}
        {f.help && <div style={{ fontSize: 11.5, color: tk.textMuted, marginTop: 3 }}>{f.help}</div>}
      </div>);
  };
  const doSign = async (step) => {
    if (!sigName.trim()) { setStepErr('Type your full legal name to sign.'); return; }
    if (!sigImg) { setStepErr('Draw your signature in the box.'); return; }
    setBusy(true); setStepErr('');
    try {
      const j = await fetch(API + '/api/staff-onboard/' + token + '/step/' + step.requirement_id, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ signature_name: sigName.trim(), signature_image: sigImg, answers: formVals }) }).then(r => r.json());
      if (j.ok) { await load(false); setIdx(idx + 1); setStepErr(''); setSigImg(null); setPadKey(k => k + 1); }
      else setStepErr(j.error || 'Could not save. Try again.');
    } catch (e) { setStepErr('Connection problem. Try again.'); }
    setBusy(false);
  };
  const doUpload = async (step) => {
    if (!file) { setStepErr('Choose or photograph the document first.'); return; }
    setBusy(true); setStepErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const j = await fetch(API + '/api/staff-onboard/' + token + '/step/' + step.requirement_id + '/upload', {
        method: 'POST', body: fd }).then(r => r.json());
      if (j.ok) { setFile(null); await load(false); setIdx(idx + 1); }
      else setStepErr(j.error || 'Upload failed. Try again.');
    } catch (e) { setStepErr('Connection problem. Try again.'); }
    setBusy(false);
  };
  const doSubmit = async () => {
    setBusy(true); setStepErr('');
    try {
      const j = await fetch(API + '/api/staff-onboard/' + token + '/submit', { method: 'POST' }).then(r => r.json());
      if (j.ok) setJustFinished(true);
      else setStepErr(j.open ? 'Still open: ' + j.open.join(', ') : (j.error || 'Could not submit.'));
    } catch (e) { setStepErr('Connection problem. Try again.'); }
    setBusy(false);
  };

  if (submitted) return wrap(
    <div style={{ ...card, padding: 24, marginTop: 14, textAlign: 'center' }}>
      <div style={{ fontSize: 40, color: tk.success }}>{CHECK}</div>
      <div style={{ fontSize: 18, fontWeight: 700, color: tk.ink, margin: '6px 0' }}>All set, {state.person.first_name}.</div>
      <div style={{ fontSize: 14, color: tk.textSecondary }}>Your onboarding packet for {state.person.org} is submitted. HR has been updated and will reach out with your start-day details. You can close this page.</div>
    </div>);

  const step = idx < steps.length ? steps[idx] : null;

  const stepList = (
    <div style={isWide ? { ...card, padding: '10px 12px', maxHeight: '70vh', overflowY: 'auto' } : { marginTop: 14 }}>
      {isWide && <div style={{ ...lab, padding: '4px 4px 8px' }}>Your packet {DOT} {done} of {steps.length}</div>}
      {steps.map((s, i) => (
        <div key={s.requirement_id} onClick={() => { setStepErr(''); setIdx(i); }}
          style={{ display: 'flex', justifyContent: 'space-between', gap: 8, padding: '8px 4px', borderBottom: '1px solid ' + tk.borderSoft,
            cursor: 'pointer', fontSize: 13, background: i === idx ? tk.aiSoft : 'transparent', borderRadius: 6 }}>
          <span style={{ color: tk.ink, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{i + 1}. {s.title}</span>
          <span style={{ color: s.done ? tk.success : tk.textMuted, fontWeight: 700, flexShrink: 0 }}>{s.done ? CHECK : '\u2013'}</span>
        </div>))}
    </div>);

  return wrap(
    <div>
      <div style={{ fontSize: 13, color: tk.textSecondary, marginBottom: 12 }}>Welcome, {state.person.first_name} {DOT} {state.person.org}</div>
      <div style={{ ...card, padding: '12px 16px', marginBottom: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, marginBottom: 6 }}>
          <span style={{ fontWeight: 600, color: tk.ink }}>Onboarding packet</span>
          <span style={{ color: tk.textSecondary, fontVariantNumeric: 'tabular-nums' }}>{done} of {steps.length}</span>
        </div>
        <div style={{ height: 8, borderRadius: 4, background: tk.surfaceSoft, overflow: 'hidden' }}>
          <div style={{ width: pct + '%', height: '100%', background: pct === 100 ? tk.success : tk.ai, transition: 'width 0.3s' }} />
        </div>
      </div>

      <div style={isWide ? { display: 'grid', gridTemplateColumns: '58fr 42fr', gap: 16, alignItems: 'start' } : undefined}>
      <div>
      {allDone ? (
        <div style={{ ...card, padding: 20 }}>
          <div style={{ fontSize: 16, fontWeight: 700, color: tk.ink, marginBottom: 6 }}>Everything's complete.</div>
          <div style={{ fontSize: 13.5, color: tk.textSecondary, marginBottom: 14 }}>Review is done step by step above; hit submit to send your packet to HR.</div>
          {stepErr && <div style={{ fontSize: 13, color: tk.danger, marginBottom: 10 }}>{stepErr}</div>}
          <button style={btn(true, busy)} disabled={busy} onClick={doSubmit}>{busy ? 'Submitting\u2026' : 'Submit my packet'}</button>
        </div>
      ) : step && (
        <div style={{ ...card, padding: 18 }}>
          <div style={{ ...lab, marginBottom: 4 }}>Step {idx + 1} of {steps.length}{step.done ? ' ' + DOT + ' already done' : ''}</div>
          <div style={{ fontSize: 17, fontWeight: 700, color: tk.ink, lineHeight: 1.3, marginBottom: 10 }}>{step.title}</div>
          {step.needs_hr && step.hr_done && step.hr_schema && (
            <div style={{ border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, background: tk.canvas, padding: '12px 14px', marginBottom: 12 }}>
              <div style={{ ...lab, marginBottom: 6 }}>{step.hr_schema.section_label || 'Employer section'} {DOT} completed by HR</div>
              <AnswerSheet schema={step.hr_schema} answers={step.hr_values || {}} isMobile={!isWide} compact />
            </div>)}
          {step.document_body && (
            <div style={{ border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, background: '#fff', padding: '14px 16px', maxHeight: '48vh', overflowY: 'auto', marginBottom: 12, fontSize: 13.5, lineHeight: 1.55, color: tk.ink, whiteSpace: 'pre-wrap' }}>
              {step.document_body}</div>)}
          {step.has_document && (
            <div style={{ marginBottom: 14 }}>
              {docUrl
                ? <a href={docUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, fontWeight: 600, color: tk.ai, textDecoration: 'none' }}>Original form (PDF) {'\u2197'}</a>
                : <span style={{ fontSize: 12, color: tk.textSecondary }}>{docErr ? '' : 'Loading the original form link\u2026'}</span>}
            </div>)}
          {step.no_variant ? (
            <div style={{ border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, background: tk.canvas, padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: tk.ink, marginBottom: 4 }}>Not ready yet</div>
              <div style={{ fontSize: 13, color: tk.textSecondary }}>The job description for your position hasn\u2019t been attached yet. HR will add it and this step will open for you to review and sign. Skip it for now and keep going.</div>
            </div>
          ) : step.needs_hr && !step.hr_done ? (
            <div style={{ border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, background: tk.canvas, padding: '14px 16px', marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: tk.ink, marginBottom: 4 }}>Waiting on HR</div>
              <div style={{ fontSize: 13, color: tk.textSecondary }}>HR fills in the employer section of this form (your pay rate, payday, workers’ compensation carrier). Once they have, this step opens for you to review and sign. Skip it for now and keep going.</div>
            </div>
          ) : step.kind === 'sign' ? (
            <div>
              {step.form_schema && step.form_schema.note &&
                <div style={{ fontSize: 12.5, color: tk.textSecondary, background: tk.canvas, border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, padding: '9px 11px', marginBottom: 12 }}>{step.form_schema.note}</div>}
              {step.form_schema && Array.isArray(step.form_schema.fields) && (
                <div style={{ marginBottom: 6 }}>{step.form_schema.fields.map(renderField)}</div>)}
              {step.form_schema && step.form_schema.instructions_url &&
                <a href={step.form_schema.instructions_url} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-block', fontSize: 13, fontWeight: 600, color: tk.ai, marginBottom: 12 }}>
                  {step.form_schema.instructions_label || 'Official form and instructions'} {'\u2197'}</a>}
              {step.form_schema && step.form_schema.jurat &&
                <div style={{ fontSize: 12.5, color: tk.ink, background: tk.canvas, border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, padding: '10px 12px', marginBottom: 12, lineHeight: 1.5 }}>
                  {step.form_schema.jurat}</div>}
              <div style={{ fontSize: 13.5, color: tk.textSecondary, marginBottom: 12 }}>
                {step.form_schema ? 'Typing your full legal name below and pressing Sign records your electronic signature on this form' + (step.form_schema.jurat ? ' under the certification above' : '') + ', with a timestamp.' : 'Read the document your HR contact provided for this item. Typing your full legal name below and pressing Sign records your electronic signature with a timestamp.'}
              </div>
              <div style={{ ...lab, marginBottom: 4 }}>Full legal name</div>
              <input style={inp} value={sigName} autoComplete="name" placeholder={state.person.first_name + ' ' + state.person.last_name}
                onChange={e => setSigName(e.target.value)} />
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', margin: '12px 0 4px' }}>
                <span style={lab}>Draw your signature</span>
                <button style={{ font: 'inherit', fontSize: 12, fontWeight: 600, color: tk.textSecondary, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
                  onClick={() => { setSigImg(null); setPadKey(k => k + 1); }}>Clear</button>
              </div>
              <div style={{ border: '1px solid ' + tk.border, borderRadius: tk.radiusControl, overflow: 'hidden', background: '#fff' }}>
                <SignaturePad key={padKey} onChange={setSigImg} height={130} />
              </div>
              {stepErr && <div style={{ fontSize: 13, color: tk.danger, marginTop: 8 }}>{stepErr}</div>}
              <div style={{ marginTop: 12 }}>
                <button style={btn(true, busy)} disabled={busy} onClick={() => doSign(step)}>{busy ? 'Saving\u2026' : 'Sign ' + DASH + ' ' + (sigName.trim() || 'type your name')}</button>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 13.5, color: tk.textSecondary, marginBottom: 12 }}>
                Photograph or upload this document. Clear, readable photos are fine.
              </div>
              <input type="file" accept="image/*,.pdf" style={{ fontSize: 14, marginBottom: 10, maxWidth: '100%' }}
                onChange={e => setFile(e.target.files && e.target.files[0])} />
              {stepErr && <div style={{ fontSize: 13, color: tk.danger, marginBottom: 8 }}>{stepErr}</div>}
              <button style={btn(true, busy)} disabled={busy} onClick={() => doUpload(step)}>{busy ? 'Uploading\u2026' : 'Upload'}</button>
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {idx > 0 && <button style={{ ...btn(false, false), width: 'auto', minHeight: 40, padding: '8px 14px', fontSize: 13 }} onClick={() => { setStepErr(''); setIdx(idx - 1); }}>Back</button>}
            <button style={{ ...btn(false, false), width: 'auto', minHeight: 40, padding: '8px 14px', fontSize: 13 }} onClick={() => { setStepErr(''); setIdx(idx + 1); }}>Skip for now</button>
          </div>
        </div>
      )}

      {!isWide && stepList}
      </div>
      {isWide && stepList}
      </div>
      <div style={{ fontSize: 11.5, color: tk.textMuted, marginTop: 14, textAlign: 'center' }}>Questions? Contact your HR team. This link is private to you {DASH} don't forward it.</div>
    </div>);
}
